import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MdClose,
  MdVisibility,
  MdVisibilityOff,
  MdAutorenew,
  MdContentCopy,
} from 'react-icons/md';
import toast from 'react-hot-toast';
import axios from '../lib/axios';
import { endpoints } from '../constants/endpoints';
import { UserRole } from '../constants/roles';
import { generatePassword } from '../utils/generatePassword';
import type { User, CreateUserRequest, UpdateUserRequest } from '../types/auth';

interface EditUserModalProps {
  user: User | null;
  onClose: () => void;
}

export default function EditUserModal({ user, onClose }: EditUserModalProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CreateUserRequest>();

  useEffect(() => {
    if (user) {
      setValue('first_name', user.first_name);
      setValue('last_name', user.last_name);
      setValue('email', user.email);
      setValue('password', '');
      setNewPassword(null);
      setShowPassword(false);
    }
  }, [user, setValue]);

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateUserRequest) => {
      return axios.patch(endpoints.users.update(user!.id), data);
    },
    onSuccess: () => {
      toast.success('User updated');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const handleGeneratePassword = () => {
    const password = generatePassword();
    setValue('password', password);
    setShowPassword(true);
  };

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleClose = () => {
    reset();
    setNewPassword(null);
    setShowPassword(false);
    onClose();
  };

  const onSubmit = (data: CreateUserRequest) => {
    const payload: UpdateUserRequest = {};

    if (data.first_name !== user!.first_name) {
      payload.first_name = data.first_name;
    }
    if (data.last_name !== user!.last_name) {
      payload.last_name = data.last_name;
    }
    if (!isSuperAdmin && data.email !== user!.email) {
      payload.email = data.email;
    }
    if (data.password) {
      payload.password = data.password;
      setNewPassword(data.password);
    }

    if (Object.keys(payload).length === 0) {
      toast.error('No changes to save');
      return;
    }

    updateMutation.mutate(payload);
  };

  // Auto-close when update succeeds without password change
  useEffect(() => {
    if (updateMutation.isSuccess && !newPassword) {
      reset();
      setShowPassword(false);
      onClose();
    }
  }, [updateMutation.isSuccess, newPassword, reset, onClose]);

  const isSuperAdmin = user?.role === UserRole.SUPERADMIN;

  if (!user) return null;

  // Success state — show new password if it was changed
  if (updateMutation.isSuccess && newPassword) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
        <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Password Updated
            </h3>
            <button
              onClick={handleClose}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <MdClose size={20} />
            </button>
          </div>

          <p className="mb-4 text-sm text-gray-600">
            Copy the new password and send it to the user.
          </p>

          <div className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2">
            <div>
              <div className="text-xs text-gray-500">New Password</div>
              <div className="text-sm font-medium text-gray-900">
                {newPassword}
              </div>
            </div>
            <button
              onClick={() => handleCopy(newPassword)}
              className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
              title="Copy password"
            >
              <MdContentCopy size={16} />
            </button>
          </div>

          <button
            onClick={handleClose}
            className="mt-6 w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Edit User</h3>
          <button
            onClick={handleClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <MdClose size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="edit_first_name"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  First Name
                </label>
                <input
                  id="edit_first_name"
                  type="text"
                  {...register('first_name', {
                    required: 'First name is required',
                  })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                {errors.first_name && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.first_name.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="edit_last_name"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Last Name
                </label>
                <input
                  id="edit_last_name"
                  type="text"
                  {...register('last_name', {
                    required: 'Last name is required',
                  })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                {errors.last_name && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.last_name.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="edit_email"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <input
                id="edit_email"
                type="email"
                disabled={isSuperAdmin}
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                className={`w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${isSuperAdmin ? 'cursor-not-allowed bg-gray-100 text-gray-500' : ''}`}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="edit_password"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Password
                <span className="ml-1 font-normal text-gray-400">
                  (leave blank to keep current)
                </span>
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    id="edit_password"
                    type={showPassword ? 'text' : 'password'}
                    {...register('password', {
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters',
                      },
                    })}
                    placeholder="Enter new password"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 pr-9 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <MdVisibilityOff size={18} />
                    ) : (
                      <MdVisibility size={18} />
                    )}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleGeneratePassword}
                  className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                  title="Generate password"
                >
                  <MdAutorenew size={16} />
                  Generate
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.password.message}
                </p>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
